import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import TimeIntervalField from 'components/Utils/TimeIntervalField';

/**
 * Shared create/edit modal for a concern requirement ("clearance"). Owns the form state and the
 * create/update dispatch so the form lives in exactly one place — used both by the Concern
 * Requirements admin page and by the requirements picker when configuring a concern.
 *
 * Pass `requirement` to edit an existing one, or null/undefined to create a new one. `onSaved`
 * (optional) receives the created/updated requirement so callers can react (e.g. attach the new
 * clearance to the concern being configured); the redux store is updated by the thunk either way.
 */
const ConcernRequirementModal = ({ isOpen, requirement, onClose, onSaved }) => {
  const dispatch = useAppDispatch();

  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [patientMessage, setPatientMessage] = useState('');
  const [defaultUses, setDefaultUses] = useState('');
  const [defaultExpiresAfter, setDefaultExpiresAfter] = useState(null);

  // Seed the form from the target requirement each time the modal opens (blank for create).
  useEffect(() => {
    if (!isOpen) return;
    setLabel(requirement?.label || '');
    setDescription(requirement?.description || '');
    setPatientMessage(requirement?.patient_message || '');
    setDefaultUses(requirement?.default_uses != null ? String(requirement.default_uses) : '');
    setDefaultExpiresAfter(requirement?.default_expires_after ?? null);
  }, [isOpen, requirement]);

  const handleSave = async () => {
    if (!label.trim()) return;
    const concernRequirement = {
      label: label.trim(),
      description: description.trim() || undefined,
      patient_message: patientMessage.trim() || undefined,
      default_uses: defaultUses !== '' ? Number(defaultUses) : undefined,
      default_expires_after: defaultExpiresAfter ?? undefined,
    };
    const result = requirement
      ? await dispatch(
          api.Appointments.updateConcernRequirement({ id: requirement.id, concernRequirement }),
        )
      : await dispatch(api.Appointments.createConcernRequirement({ concernRequirement }));
    if (result?.payload) onSaved?.(result.payload);
    onClose();
  };

  return (
    <DefaultModal
      isOpen={isOpen}
      onRequestClose={onClose}
      title={requirement ? 'Edit Requirement' : 'Create Requirement'}
      wrap
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      className="fit-modal"
      content={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', minWidth: '300px' }}>
          <DefaultTextField
            name="requirement_label"
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: '100%' }}
          />
          <DefaultTextField
            name="requirement_description"
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: '100%' }}
          />
          <DefaultTextField
            name="requirement_patient_message"
            label="Patient Message (optional)"
            value={patientMessage}
            onChange={(e) => setPatientMessage(e.target.value)}
            style={{ width: '100%' }}
            multiline
            rows={2}
            placeholder="Message shown to patients without this clearance"
          />
          <DefaultTextField
            name="requirement_default_uses"
            label="Default Uses (optional)"
            value={defaultUses}
            onChange={(e) => setDefaultUses(e.target.value)}
            style={{ width: '100%' }}
            type="number"
            placeholder="Number of uses when clearance is granted"
          />
          <div>
            <div className="small-title">Default Expiry (optional)</div>
            <TimeIntervalField
              label="Expiry"
              value={defaultExpiresAfter}
              onChange={setDefaultExpiresAfter}
              min_interval="days"
              valueWidth="100px"
            />
          </div>
          <DefaultButton
            name={requirement ? 'Save' : 'Create'}
            className="blue"
            onClick={handleSave}
            disabled={!label.trim()}
          />
        </div>
      }
    />
  );
};

export default ConcernRequirementModal;
